"use client";

import { trackUmamiEvent, trackUmamiEventBeacon } from "@analytics";

export type ClientErrorSource =
	| "window"
	| "unhandledrejection"
	| "boundary"
	| "global-boundary";

// Umami stores event_data strings in a bounded column; long stacks get dropped
// silently, so trim before sending rather than after.
const MAX_MESSAGE = 200;
const MAX_FRAME = 200;

// A render loop can throw the same error hundreds of times a second. Each
// distinct source+message reports once and no more, and the cap on distinct
// errors stops one bad page from flooding the dashboard.
const MAX_DISTINCT = 5;

const seen = new Set<string>();

// WebKit rejects every in-flight fetch with "Load failed" both when the tab
// comes back from the background and when the page is navigating away, so the
// message alone can't tell those apart from a real network drop. Module-level
// so it survives global-error swapping out the root layout (and ErrorMonitor).
let lastHiddenAt = 0;
let leaving = false;
// pagehide fires only once the old page is on its way out, so an error thrown
// the moment a navigation starts still reads leaving=false. The last tap
// (what and how long ago) covers that gap: "a:https://accounts.google.com…"
// 300ms before the error means the user was leaving. Radix dropdown items are
// divs with role="menuitem", so they count as taps too.
let lastClickAt = 0;
let lastClick = "";
if (typeof window !== "undefined") {
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") {
			lastHiddenAt = Date.now();
		}
	});
	document.addEventListener(
		"click",
		(event) => {
			const el =
				event.target instanceof Element
					? event.target.closest('a, button, [role="menuitem"]')
					: null;
			lastClickAt = Date.now();
			lastClick = !el
				? ""
				: el.tagName === "A"
					? `a:${el.getAttribute("href") ?? ""}`
					: `${el.tagName === "BUTTON" ? "button" : "menuitem"}:${(el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim()}`;
		},
		{ capture: true },
	);
	window.addEventListener("pagehide", () => {
		leaving = true;
	});
	// Restored from the back/forward cache: the page is live again.
	window.addEventListener("pageshow", () => {
		leaving = false;
	});
}

function pageState() {
	return {
		visibility: document.visibilityState,
		hiddenAgoMs: lastHiddenAt ? Date.now() - lastHiddenAt : -1,
		leaving,
		online: navigator.onLine,
		uptimeMs: Math.round(performance.now()),
		lastClickAgoMs: lastClickAt ? Date.now() - lastClickAt : -1,
		lastClick: truncate(lastClick, 80),
	};
}

function truncate(value: string, max: number) {
	return value.length > max ? `${value.slice(0, max)}…` : value;
}

// V8 frames read "    at fn (url:1:2)"; WebKit and Gecko write "fn@url:1:2".
// Matching only the V8 shape left every Safari error with an empty frame.
const FRAME_LINE = /^\s*at |@\S+:\d+:\d+/;

function describe(error: unknown): { message: string; frame: string } {
	if (error instanceof Error) {
		// First stack frame is the only part that helps locate the code; the
		// rest is framework noise and won't fit anyway.
		const frame =
			error.stack?.split("\n").find((line) => FRAME_LINE.test(line)) ??
			"";
		return {
			message: truncate(`${error.name}: ${error.message}`, MAX_MESSAGE),
			frame: truncate(frame.trim(), MAX_FRAME),
		};
	}
	return { message: truncate(String(error), MAX_MESSAGE), frame: "" };
}

/**
 * Report a client-side crash to Umami so it shows up alongside the rest of our
 * funnel events. Before this existed these errors only ever hit the console,
 * which meant production crashes were invisible unless someone happened to
 * watch a Clarity replay.
 */
export function reportClientError(
	source: ClientErrorSource,
	error: unknown,
	extra?: Record<string, unknown>,
) {
	// The reporter must never be the thing that breaks the page.
	try {
		const { message, frame } = describe(error);

		console.error(`[client-error:${source}]`, error);

		// Keyed on source+message, not the stack: in a minified bundle the same
		// bug can surface from several frames, and we only need to learn it
		// happened. That also means a repeat of the same crash never reports
		// twice, so the distinct cap below is the only ceiling there is.
		const key = `${source}|${message}`;
		if (seen.has(key) || seen.size >= MAX_DISTINCT) {
			return;
		}
		seen.add(key);

		// The global boundary replaces the root layout, taking the Rybbit script
		// with it — the queue in trackUmamiEvent would never flush there. A
		// boundary that is about to reload the page needs the beacon too.
		const track =
			source === "global-boundary" || extra?.reloading === true
				? trackUmamiEventBeacon
				: trackUmamiEvent;

		void track("client-error", {
			source,
			message,
			frame,
			path:
				typeof window === "undefined"
					? ""
					: truncate(window.location.pathname, MAX_MESSAGE),
			// Auto-translated pages are a known crash source: the translator
			// swaps out text nodes React still holds references to.
			translated:
				typeof document === "undefined"
					? false
					: document.documentElement.classList.contains(
							"translated-ltr",
						) ||
						document.documentElement.classList.contains(
							"translated-rtl",
						) ||
						!!document.querySelector(
							"font[style*='vertical-align']",
						),
			...(typeof window === "undefined" ? {} : pageState()),
			...extra,
		});
	} catch {
		// Swallow: reporting is best-effort.
	}
}
